import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Table, Modal, Form, Input, Popconfirm } from "antd";
import type { ColumnsType, TableProps } from 'antd/es/table';
import { AuthContext } from "../../config/AuthContext";
import Colors from "../../styles/Colors";
import { BiRefresh } from "react-icons/bi";
import toast from "react-hot-toast";

const apiUrl = process.env.REACT_APP_API_URL;

interface CompteAct {
    id: number;
    idact_holded: string;
    idacti: string;
    codcen: string;
    descripcio: string;
    compte: string;
    descripcio_compte: string;
}

const CompteActTable = () => {
    const { user } = useContext(AuthContext);
    const [comptes, setComptes] = useState<CompteAct[]>([]);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingRow, setEditingRow] = useState<CompteAct | null>(null);
    const [isInserting, setIsInserting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            fetchComptes();
        }
    }, [visible]);

    async function fetchComptes() {
        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}:8071/api/facturacio/comptes`, {
                headers: {
                    'pantalla': window.location.href,
                    'idusuari': user.id.toString()
                }
            });
            if (response.ok) {
                const data = await response.json();
                setComptes(data);
            }
        } catch (error) {
            toast.error("Error al carregar els comptes.");
        }
        setLoading(false);
    }

    async function handleUpdate(values: any) {
        setLoading(true);
        try {
            const url = isInserting ? `${apiUrl}:8071/api/facturacio/insert-compte` : `${apiUrl}:8071/api/facturacio/update-compte`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'pantalla': window.location.href,
                    'idusuari': user.id.toString()
                },
                body: JSON.stringify({
                    id: editingRow?.id,
                    ...values
                })
            });

            if (response.ok) {
                toast.success(isInserting ? "Compte creat correctament." : "Compte actualitzat correctament.");
                setEditModalVisible(false);
                fetchComptes();
            } else {
                toast.error("Error al desar el compte.");
            }
        } catch (error) {
            toast.error("Error al desar el compte.");
        }
        setLoading(false);
    }

    async function handleDelete(id: number) {
        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}:8071/api/facturacio/delete-compte`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'pantalla': window.location.href,
                    'idusuari': user.id.toString()
                },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                toast.success("Compte eliminat correctament.");
                fetchComptes();
            } else {
                toast.error("Error al eliminat.");
            }
        } catch (error) {
            toast.error("Error al eliminat.");
        }
        setLoading(false);
    }

    const columns: ColumnsType<CompteAct> = [
        {
            key: 'descripcio', title: 'Descripció (XML)', dataIndex: 'descripcio',
        },
        {
            key: 'idact_holded', title: 'ID Activitat Holded', dataIndex: 'idact_holded',
        },
        {
            key: 'compte', title: 'Compte Contable', dataIndex: 'compte',
        },
        {
            key: 'descripcio_compte', title: 'Descripció Compte', dataIndex: 'descripcio_compte',
        },
        {
            key: 'actions', title: 'Accions', width: 150,
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button size="small" type="primary" onClick={() => {
                        setEditingRow(record);
                        setIsInserting(false);
                        form.setFieldsValue({
                            idact_holded: record.idact_holded,
                            descripcio: record.descripcio,
                            compte: record.compte,
                            descripcio_compte: record.descripcio_compte
                        });
                        setEditModalVisible(true);
                    }}>Editar</Button>
                    <Popconfirm
                        title="Eliminar Compte"
                        description="N'estàs segur d'eliminar aquest compte?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button size="small" danger>Eliminar</Button>
                    </Popconfirm>
                </div>
            )
        }
    ];

    const tableProps: TableProps<CompteAct> = {
        loading,
        rowKey: 'id'
    };

    function toggleTable() {
        setVisible(!visible);
    }

    return (
        <Card bordered={true} className={"elemento"}>
            <div className={"actionButtons"}>
                <Button title={"Nou Compte"} style={{backgroundColor: Colors.colors.color6, marginRight: '10px'}} onClick={() => {
                    setEditingRow(null);
                    setIsInserting(true);
                    form.resetFields();
                    setEditModalVisible(true);
                }}>
                    + Nou Compte
                </Button>
                <Button title={"Refrescar"} style={{backgroundColor: Colors.colors.color4}} onClick={fetchComptes}>
                    <BiRefresh/>
                </Button>
            </div>
            <h4 className={"subtitol"} onClick={toggleTable}>Configuració de Comptes d'Activitat</h4>
            
            {visible && (
                <Table
                    {...tableProps}
                    columns={columns}
                    dataSource={comptes}
                />
            )}

            <Modal
                title={isInserting ? "Nou Compte d'Activitat" : "Editar Compte d'Activitat"}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={() => form.submit()}
                okText="Desar Canvis"
                cancelText="Cancel·lar"
            >
                <Form layout="vertical" form={form} onFinish={handleUpdate}>
                    <Form.Item label="Descripció (XML)" name="descripcio" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="ID Activitat Holded" name="idact_holded">
                        <Input />
                    </Form.Item>
                    <Form.Item label="Compte Contable" name="compte">
                        <Input />
                    </Form.Item>
                    <Form.Item label="Descripció Compte" name="descripcio_compte">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default CompteActTable;
